import { useTranslation } from 'react-i18next';

import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import type { SupportTotals } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { useDurationFormat } from '../hooks/use-duration-format';

import { ChangeIndicator } from './change-indicator';

type SupportKpi =
  'chats' | 'missedRate' | 'firstResponse' | 'csat' | 'tickets' | 'attributedNet' | 'deflections';

const KPIS: readonly SupportKpi[] = [
  'chats',
  'missedRate',
  'firstResponse',
  'csat',
  'tickets',
  'attributedNet',
  'deflections',
];

const LOWER_IS_BETTER = new Set<SupportKpi>(['missedRate', 'firstResponse']);

export interface SupportKpisProps {
  totals: SupportTotals | undefined;
  previous: SupportTotals | undefined;
  isLoading: boolean;
}

/** The support totals next to their change against the previous period of the same length. */
export function SupportKpis({ totals, previous, isLoading }: SupportKpisProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const duration = useDurationFormat();
  const percent = (value: number | null) =>
    value === null ? '—' : format.percent(value, { maximumFractionDigits: 1 });

  const figure = (key: SupportKpi, now: SupportTotals, before: SupportTotals) => {
    switch (key) {
      case 'chats':
        return {
          value: format.number(now.chatsStarted),
          current: now.chatsStarted,
          previous: before.chatsStarted,
        };
      case 'missedRate':
        return {
          value: percent(now.missedRate),
          current: now.missedRate,
          previous: before.missedRate,
        };
      case 'firstResponse':
        return {
          value: duration(now.firstResponseSecondsMedian),
          current: now.firstResponseSecondsMedian,
          previous: before.firstResponseSecondsMedian,
        };
      case 'csat':
        return { value: percent(now.csat), current: now.csat, previous: before.csat };
      case 'tickets':
        return {
          value: format.number(now.ticketsOpened),
          current: now.ticketsOpened,
          previous: before.ticketsOpened,
        };
      case 'attributedNet':
        return {
          value: format.money(now.attributedNet),
          current: now.attributedNet.amount,
          previous: before.attributedNet.amount,
        };
      case 'deflections':
        return {
          value: format.number(now.helpDeflections),
          current: now.helpDeflections,
          previous: before.helpDeflections,
        };
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {KPIS.map((key) => {
        const f = totals && previous ? figure(key, totals, previous) : undefined;
        return (
          <div key={key} role="group" aria-label={t(`support.kpi.${key}`)} className="contents">
            <StatCard
              label={t(`support.kpi.${key}`)}
              value={f?.value ?? '—'}
              detail={
                f ? (
                  <ChangeIndicator
                    current={f.current}
                    previous={f.previous}
                    lowerIsBetter={LOWER_IS_BETTER.has(key)}
                  />
                ) : (
                  ''
                )
              }
              isLoading={isLoading}
            />
          </div>
        );
      })}
    </div>
  );
}
